import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import router from "@/router";
import type {
  ChangeTariffPayload,
  Service,
  TariffArray,
} from "@/struct/TariffStruct";
import { formatAmountInput } from "@/utils/format";

export function useTariffPage() {
  const store = useStore();
  const route = useRoute();
  const { t } = useI18n();

  const mainTariff = computed(() => store.getters["tariff/mainTariff"]);
  const tariffPriceDiscount = computed(
    () => store.getters["tariff/tariffPriceDiscount"]
  );
  const discountAmount = computed(
    () => mainTariff.value?.discount?.amount || 0
  );
  const discountName = computed(() => mainTariff.value?.discount?.name || "");
  const subscriptionDetails = computed(
    () => store.state.tariff.subscriptionDetails
  );
  const renewalDate = computed(
    () => subscriptionDetails.value.mainProduct.renewalDate || ""
  );
  const tariffServices = ref<{ text: string; value: number }[]>([]);

  const isChangeTariff = ref(false);
  const isDetailsTariff = ref(false);
  const isSticky = ref(false);

  const modalsList = ref<{ [key: string]: boolean }>({
    changeModal: false,
    poorModal: false,
    burnModal: false,
    successModal: false,
    commonError: false,
    sameTariff: false,
    regionError: false,
    notFound: false,
    noPlan: false,
  });

  const changeTariffNowLoading = ref<boolean>(false);
  const changeTariffLaterLoading = ref<boolean>(false);

  const availableTariffs = computed(() => store.state.tariff.availableTariffs);

  const tariffsAvailable = ref<TariffArray[]>([]);
  const chosenTariff = ref<TariffArray>();

  const returnServiceIcons = (services: Service[]) => {
    const availableServices: string[] = [];
    services.forEach((item) => {
      switch (item.value) {
        case 1:
          availableServices.push("yandex-maps");
          break;
        case 2:
          availableServices.push("viber", "tg", "whatsapp", "max");
          break;
        case 3:
          availableServices.push("vk", "ok");
          break;
        case 4:
          availableServices.push("yandex-music");
          break;
        case 5:
          availableServices.push("rutube");
          break;
        default:
          break;
      }
    });
    return availableServices;
  };

  const getUnits = (id: "gb" | "min" | "sms") => {
    switch (id) {
      case "gb":
        return t("common.units.gb");
      case "min":
        return t("common.units.min");
      case "sms":
        return t("common.units.sms");
    }
  };

  const startChange = (item: TariffArray) => {
    chosenTariff.value = item;
    modalsList.value.changeModal = true;
  };

  const closeModals = () => {
    Object.keys(modalsList.value).forEach((key) => {
      modalsList.value[key] = false;
    });
  };

  const toPayment = () => {
    store.dispatch("togglePaymentModal", true);
    closeModals();
  };
  const changeTariffRequest = (payload: ChangeTariffPayload) => {
    store
      .dispatch("tariff/changeTariff", payload)
      .then((res) => {
        console.log(res.data);
        modalsList.value.successModal = true;
      })
      .catch((err) => {
        console.log(err);
        switch (err.response.data.code) {
          case 52:
            modalsList.value.sameTariff = true;
            break;
          case 53:
            modalsList.value.notFound = true;
            break;
          case 54:
            modalsList.value.poorModal = true;
            break;
          case 55:
            modalsList.value.noPlan = true;
            break;
          case 56:
            modalsList.value.regionError = true;
            break;
          default:
            modalsList.value.commonError = true;
        }
      })
      .finally(() => {
        modalsList.value.changeModal = false;
        changeTariffNowLoading.value = false;
        changeTariffLaterLoading.value = false;
      });
  };
  const updateSubscriptionDetails = async () => {
    await store.dispatch("tariff/updateSubscriptionDetails");
  };
  const doChange = async (timing: "now" | "later") => {
    console.log(timing);
    changeTariffNowLoading.value = true;
    changeTariffLaterLoading.value = true;
    await updateSubscriptionDetails();
    if (chosenTariff.value) {
      await changeTariffRequest({
        mainProductId: chosenTariff.value.ems_product_id,
        scheduledOn: timing === "later" ? renewalDate.value : "",
      });
    } else {
      closeModals();
    }
  };

  const tariffDetails = (item: TariffArray) => {
    chosenTariff.value = item;
    isDetailsTariff.value = true;
  };

  const updateOffers = () => {
    store.dispatch("tariff/updateOffers");
  };

  const handleScroll = () => {
    isSticky.value = window.scrollY > 120;
  };

  const updateFullData = async () => {
    if (!mainTariff.value.title || !mainTariff.value.options) {
      await router.push("account");
    }
    await updateOffers();
    window.addEventListener("scroll", handleScroll);
    console.log(mainTariff.value);
    if (mainTariff.value.service) {
      console.log("service");
      console.log(mainTariff.value.service);
      tariffServices.value = mainTariff.value.service;
    }
  };

  watch(
    () => mainTariff.value,
    (newData) => {
      if (newData) updateFullData();
    }
  );

  onMounted(() => {
    updateFullData();
    if (route.query.change) {
      isChangeTariff.value = true;
    }
  });

  onUnmounted(() => {
    window.removeEventListener("scroll", handleScroll);
  });

  const pageTitle = computed(() => {
    let res = t("tariff.page.titleCurrent");
    if (isChangeTariff.value) {
      res = t("tariff.page.titleChoose");
    }
    if (isDetailsTariff.value) {
      res = t("tariff.page.titleChange");
    }
    return res;
  });

  return {
    changeTariffLaterLoading,
    changeTariffNowLoading,
    chosenTariff,
    closeModals,
    discountAmount,
    discountName,
    doChange,
    formatAmountInput,
    getUnits,
    isChangeTariff,
    isDetailsTariff,
    isSticky,
    mainTariff,
    modalsList,
    pageTitle,
    returnServiceIcons,
    startChange,
    tariffDetails,
    tariffPriceDiscount,
    tariffServices,
    tariffsAvailable,
    t,
    toPayment,
    updateOffers,
    availableTariffs,
  };
}
